package com.macrofocus.eflux.data.quest

import com.macrofocus.eflux.LagUnit
import com.macrofocus.eflux.network.DerivedBranchColumn
import com.macrofocus.eflux.network.DerivedBusColumn
import com.macrofocus.eflux.network.logical.LogicalBranchId
import com.macrofocus.eflux.network.physical.*
import korlibs.time.DateFormat
import korlibs.time.DateTime
import kotlinx.coroutines.Deferred
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import org.molap.dataframe.DataFrame
import org.molap.dataframe.rowmajor.AccessorRowMajorDataFrame
import org.molap.dataframe.rowmajor.RowMajorDataFrame
import kotlin.time.ExperimentalTime

expect suspend fun loadBus(
    bus: BusId,
    date: String,
    lagUnit: LagUnit
): DataFrame<Int, String, Any?>

expect suspend fun loadLogicalBranch(
    branch: LogicalBranchId,
    date: String,
    lagUnit: LagUnit
): DataFrame<Int, String, Any?>

suspend fun loadBusDataFrames(
    bus: BusId,
    date: String,
    lagUnit: LagUnit
): DataFrame<Bus, RowMajorDataFrame.Column<Bus, Any?>, Any?> {
    val dataFrame = loadBus(bus, date, lagUnit)

    val busDataFrame: AccessorRowMajorDataFrame<Bus, RowMajorDataFrame.Column<Bus, Any?>, Any?> =
        AccessorRowMajorDataFrame<Bus, RowMajorDataFrame.Column<Bus, Any?>, Any?>(
            arrayOf(DerivedBusColumn.datetime,
                *DerivedBusColumn.timeseries.filterIsInstance<RowMajorDataFrame.Column<Bus, Any?>>()
                    .toTypedArray())
        )

    for (row in dataFrame.rows()) {
        // We not fetch any zone infoformation
//        val fromZoneRecord = QuestZonePowerFlowRecord(dataFrame, row)
//        val zone = Zone(fromZoneRecord)
        val zone: Zone? = null
        val fromBusRecord = QuestBusPowerFlowRecord(dataFrame, row)
        val from: Bus = Bus(fromBusRecord, zone)
        busDataFrame.addRow(from)
    }

    return busDataFrame
}

suspend fun loadLogicalBranchDataFrames(
    branch: LogicalBranchId,
    date: String,
    lagUnit: LagUnit
): Triple<DataFrame<Bus, RowMajorDataFrame.Column<Bus, Any?>, Any?>, DataFrame<Bus, RowMajorDataFrame.Column<Bus, Any?>, Any?>, DataFrame<Branch, RowMajorDataFrame.Column<Branch, Any?>, Any?>> {
    val dataFrame = loadLogicalBranch(branch, date, lagUnit)

    val fromDataFrame: AccessorRowMajorDataFrame<Bus, RowMajorDataFrame.Column<Bus, Any?>, Any?> =
        AccessorRowMajorDataFrame<Bus, RowMajorDataFrame.Column<Bus, Any?>, Any?>(
            arrayOf(DerivedBusColumn.datetime,
                    *DerivedBusColumn.timeseries.filterIsInstance<RowMajorDataFrame.Column<Bus, Any?>>()
                        .toTypedArray())
        )

    val toDataFrame: AccessorRowMajorDataFrame<Bus, RowMajorDataFrame.Column<Bus, Any?>, Any?> =
        AccessorRowMajorDataFrame<Bus, RowMajorDataFrame.Column<Bus, Any?>, Any?>(
            arrayOf(DerivedBusColumn.datetime,
                    *DerivedBusColumn.timeseries.filterIsInstance<RowMajorDataFrame.Column<Bus, Any?>>()
                        .toTypedArray())
        )

    val branchDataFrame: AccessorRowMajorDataFrame<Branch, RowMajorDataFrame.Column<Branch, Any?>, Any?> =
        AccessorRowMajorDataFrame<Branch, RowMajorDataFrame.Column<Branch, Any?>, Any?>(
            arrayOf(DerivedBranchColumn.datetime, *DerivedBranchColumn.timeseries.filterIsInstance<RowMajorDataFrame.Column<Branch, Any?>>()
                .toTypedArray())
        )

    for (row in dataFrame.rows()) {
        val fromZoneRecord = QuestZonePowerFlowRecord(dataFrame, row)
        val fromBusRecord = QuestBusPowerFlowRecord(dataFrame, row)
        val from: Bus = Bus(fromBusRecord, Zone(fromZoneRecord))
        fromDataFrame.addRow(from)

        val toZoneRecord = QuestZonePowerFlowRecord(dataFrame, row, "1")
        val toBusRecord = QuestBusPowerFlowRecord(dataFrame, row, "1")
        val to: Bus = Bus(toBusRecord, Zone(toZoneRecord))
        toDataFrame.addRow(to)

        val branchRecord = QuestBranchPowerFlowRecord(dataFrame, row)
        val branch: Branch = Branch(from, to, branchRecord)
        branchDataFrame.addRow(branch)
    }

    return Triple(fromDataFrame, toDataFrame, branchDataFrame)
}

suspend fun parse(date: String): QuestPowerFlowParser = coroutineScope {
    println("Loading zone at ${DateTime.now().format(DateFormat.DEFAULT_FORMAT)}")
    val zones = async { loadZones(date) }
    println("Loading branches at ${DateTime.now().format(DateFormat.DEFAULT_FORMAT)}")
    val branches = async { loadLogicalBranches(date) }
    println("Loading buses at ${DateTime.now().format(DateFormat.DEFAULT_FORMAT)}")
    val buses = async { loadBuses(date) }

    val zonesDataFrame = zones.await()
    val busesDataFrame = buses.await()
    val branchesDataFrame = branches.await()
    val parser = QuestPowerFlowParser(zonesDataFrame, busesDataFrame, branchesDataFrame)
    parser
}

@OptIn(ExperimentalTime::class)
suspend fun loadPhysicalNetworkFromDatabase(date: String, coordinates: Deferred<DataFrame<Int,String,Any?>>? = null, itcm: Deferred<DataFrame<Int,String,Any?>>? = null): PhysicalNetwork {
    println("Loading physical network at $date")
    val physical: PhysicalNetwork
//    with(measureTime {
        physical = PhysicalNetwork(parse(date), coordinates?.await(), itcm?.await())
//    }) {
//        println("Physical network created in $this")
//    }
    return physical
}
